import React, { useEffect, useState, useRef } from 'react';
import { format } from 'date-fns';
import { dbService, type CallSummary } from '../lib/database';
import { RefreshCw, Users, Play, Pause, Clock, Phone, CheckCircle2, XCircle, FileText, Save, Filter, Search } from 'lucide-react';

interface AdminCallLogsProps {
  userEmail: string;
}

interface PopupProps {
  call: CallSummary;
  position: { x: number; y: number };
  onClose: () => void;
  onNotesUpdate: (callId: string, notes: string) => void;
}

function CallDetailsPopup({ call, position, onClose, onNotesUpdate }: PopupProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [notes, setNotes] = useState(call.notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: position.x, y: position.y });

  const calculatePosition = () => {
    if (!popupRef.current) return;

    const popup = popupRef.current;
    const popupRect = popup.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const padding = 20;

    let x = position.x;
    let y = position.y;

    y += window.scrollY;

    if (y + popupRect.height > viewportHeight + window.scrollY) {
      y = position.y + window.scrollY - popupRect.height;
    }

    if (x + popupRect.width > viewportWidth) {
      x = viewportWidth - popupRect.width - padding;
    }

    x = Math.max(padding, x);
    y = Math.max(window.scrollY + padding, y);

    setPopupPosition({ x, y });
  };

  useEffect(() => {
    calculatePosition();
    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition);

    return () => {
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition);
    };
  }, [position]);

  const handleSaveNotes = async () => {
    setIsSaving(true);
    await onNotesUpdate(call.id, notes);
    setIsSaving(false);
  };

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current && !isDragging) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };

  const handleSliderMouseDown = () => {
    setIsDragging(true);
  };

  const handleSliderMouseUp = () => {
    setIsDragging(false);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    return timeString.slice(-4);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div 
      ref={popupRef}
      className="fixed bg-zinc-900/95 backdrop-blur-sm border border-zinc-700/50 rounded-2xl shadow-2xl p-6 w-[480px] z-50 animate-in fade-in slide-in-from-bottom-4 duration-300"
      style={{
        top: `${popupPosition.y}px`,
        left: `${popupPosition.x}px`,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-500/10 rounded-full flex items-center justify-center">
            <Phone className="w-5 h-5 text-emerald-400" />
          </div>
          <span className="text-lg font-medium text-emerald-400">{call.from_number}</span>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <div className="flex items-center text-sm text-zinc-400 mt-1">
            <Clock className="w-4 h-4 mr-1" />
            {format(new Date(call.call_time), 'MMM d, yyyy HH:mm')}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-medium text-zinc-400">Call Summary</h4>
          <p className="text-white text-sm leading-relaxed bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
            {call.call_summary}
          </p>
        </div>

        {call.call_recording_url && (
          <div className="space-y-3">
            <audio 
              ref={audioRef}
              src={call.call_recording_url}
              onEnded={() => setIsPlaying(false)}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              className="hidden"
            />
            <div className="flex items-center justify-between space-x-4">
              <button
                onClick={togglePlay}
                className="flex items-center justify-center space-x-2 bg-emerald-500/10 text-emerald-400 px-4 py-2 rounded-lg hover:bg-emerald-500/20 transition-all ring-1 ring-emerald-500/30 min-w-[120px]"
              >
                {isPlaying ? (
                  <>
                    <Pause className="w-4 h-4" />
                    <span className="font-medium">Pause</span>
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    <span className="font-medium">Play</span>
                  </>
                )}
              </button>
              <div className="flex-1 flex items-center space-x-2">
                <span className="text-xs text-zinc-400 w-12">{formatTime(currentTime)}</span>
                <div className="flex-1 relative">
                  <input
                    type="range"
                    min="0"
                    max={duration || 100}
                    value={currentTime}
                    onChange={handleSliderChange}
                    onMouseDown={handleSliderMouseDown}
                    onMouseUp={handleSliderMouseUp}
                    onTouchStart={handleSliderMouseDown}
                    onTouchEnd={handleSliderMouseUp}
                    className="w-full h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-emerald-500
                             [&::-webkit-slider-thumb]:w-3 
                             [&::-webkit-slider-thumb]:h-3 
                             [&::-webkit-slider-thumb]:rounded-full 
                             [&::-webkit-slider-thumb]:bg-emerald-500 
                             [&::-webkit-slider-thumb]:appearance-none
                             [&::-webkit-slider-thumb]:hover:bg-emerald-400
                             [&::-webkit-slider-thumb]:transition-colors"
                  />
                  <div 
                    className="absolute top-0 left-0 h-1.5 bg-emerald-500/50 rounded-l-lg pointer-events-none"
                    style={{ width: `${(currentTime / (duration || 100)) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-zinc-400 w-12">{formatTime(duration)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-zinc-400">Notes</h4>
            <button
              onClick={handleSaveNotes}
              disabled={isSaving}
              className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-lg text-sm hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{isSaving ? 'Saving...' : 'Save'}</span>
            </button>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this call..."
            className="w-full h-24 bg-zinc-800/50 rounded-lg p-3 text-white text-sm resize-none border border-zinc-700/50 focus:border-emerald-500/50 focus:outline-none transition-colors"
          />
        </div>
      </div>
    </div>
  );
}

export function AdminCallLogs({ userEmail }: AdminCallLogsProps) {
  const [callLogs, setCallLogs] = useState<CallSummary[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<CallSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [agentDisplayNames, setAgentDisplayNames] = useState<Map<string, string>>(new Map());
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [selectedCall, setSelectedCall] = useState<{
    call: CallSummary;
    position: { x: number; y: number };
  } | null>(null);
  
  const [phoneFilter, setPhoneFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'successful' | 'failed'>('all');
  const [showFilters, setShowFilters] = useState(false);

  const handleRowClick = (e: React.MouseEvent, call: CallSummary) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setSelectedCall({
      call,
      position: {
        x: rect.left,
        y: rect.top
      }
    });
  };

  const closePopup = () => {
    setSelectedCall(null);
  };

  const fetchAgentDisplayNames = async () => {
    try {
      const displayNamesMap = await dbService.getAllAgentDisplayNames();
      setAgentDisplayNames(displayNamesMap);
      return displayNamesMap;
    } catch (err) {
      console.error('Error fetching agent display names:', err);
      return new Map();
    }
  };

  const fetchData = async () => {
    try {
      let data: CallSummary[];
      if (selectedAgent !== 'all') {
        data = await dbService.getCallSummariesByAgents([selectedAgent]);
      } else {
        data = await dbService.getAllCallSummaries();
      }
      setCallLogs(data);
      setFilteredLogs(data);
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const displayNames = await fetchAgentDisplayNames();
        await fetchData();
      } catch (err) {
        setError('Failed to initialize dashboard');
        console.error('Error initializing:', err);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      fetchData();
    }
  }, [selectedAgent]);

  useEffect(() => {
    let filtered = [...callLogs];

    if (phoneFilter) {
      filtered = filtered.filter(log => 
        log.from_number.toLowerCase().includes(phoneFilter.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(log => 
        statusFilter === 'successful' ? log.call_successful : !log.call_successful
      );
    }

    setFilteredLogs(filtered);
  }, [callLogs, phoneFilter, statusFilter]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchAgentDisplayNames(), fetchData()]);
    setIsRefreshing(false);
  };

  const handleNotesUpdate = async (callId: string, notes: string) => {
    try {
      await dbService.updateCallNotes(callId, notes);
      setCallLogs(callLogs.map(log =>
        log.id === callId ? { ...log, notes } : log
      ));
    } catch (err) {
      console.error('Error updating notes:', err);
    }
  };

  const getLastFourDigits = (duration: string) => {
    if (!duration) return '';
    return duration.slice(-4);
  };

  if (loading) {
    return <div className="p-8 text-center text-zinc-400">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-500">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex flex-col gap-6 mb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-white">All Call Logs</h2>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`p-1.5 rounded-full hover:bg-zinc-800 transition-all ${
                isRefreshing ? 'opacity-50' : ''
              }`}
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 text-emerald-400 ${
                isRefreshing ? 'animate-spin' : ''
              }`} />
            </button>
          </div>
          <div className="flex items-center gap-4">
            {agentDisplayNames.size > 0 && (
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-400" />
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-zinc-300 focus:outline-none focus:border-emerald-500"
                >
                  <option value="all">All Agents</option>
                  {Array.from(agentDisplayNames.entries()).map(([agentId, displayName]) => (
                    <option key={agentId} value={agentId}>
                      {displayName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                showFilters 
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                  : 'bg-zinc-800 text-zinc-300 border border-zinc-700'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-zinc-400">
                Phone Number
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  value={phoneFilter}
                  onChange={(e) => setPhoneFilter(e.target.value)}
                  placeholder="Search phone number..."
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-zinc-300 placeholder-zinc-500 focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-zinc-400">
                Call Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'successful' | 'failed')}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2 text-zinc-300 focus:outline-none focus:border-emerald-500"
              >
                <option value="all">All Statuses</option>
                <option value="successful">Successful</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-zinc-800 text-left">
              <th className="p-4 text-zinc-400 font-medium whitespace-nowrap">Time</th>
              <th className="p-4 text-zinc-400 font-medium">
                <div className="truncate max-w-[100px]">Call ID</div>
              </th>
              <th className="p-4 text-zinc-400 font-medium">Duration</th>
              <th className="p-4 text-zinc-400 font-medium">
                <div className="truncate max-w-[120px]">From Number</div>
              </th>
              <th className="p-4 text-zinc-400 font-medium">Call Status</th>
              <th className="p-4 text-zinc-400 font-medium">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {filteredLogs.map((log) => (
              <tr 
                key={log.id} 
                className="hover:bg-zinc-800/50 relative cursor-pointer"
                onClick={(e) => handleRowClick(e, log)}
              >
                <td className="p-4 text-zinc-300 text-sm whitespace-nowrap">
                  {format(new Date(log.call_time), 'MMM d, HH:mm')}
                </td>
                <td className="p-4 text-zinc-300">
                  <div className="truncate max-w-[100px]" title={log.call_id}>
                    {log.call_id}
                  </div>
                </td>
                <td className="p-4 text-zinc-300">{getLastFourDigits(log.call_duration)}</td>
                <td className="p-4 text-zinc-300">
                  <div className="truncate max-w-[120px]" title={log.from_number}>
                    {log.from_number}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center">
                    {log.call_successful ? (
                      <div className="flex items-center text-emerald-400">
                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                        <span>Successful</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-red-400">
                        <XCircle className="w-4 h-4 mr-1.5" />
                        <span>Failed</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  {log.notes ? (
                    <div className="flex items-center text-zinc-300">
                      <FileText className="w-4 h-4 mr-1.5 text-emerald-400" />
                      <span className="truncate max-w-[200px]">{log.notes}</span>
                    </div>
                  ) : (
                    <span className="text-zinc-500">No notes</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {selectedCall && (
          <CallDetailsPopup 
            {...selectedCall}
            onClose={closePopup}
            onNotesUpdate={handleNotesUpdate}
          />
        )}
      </div>
    </div>
  );
}